import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import './kernelCapsLock.css';

/**
 * Avviso «Blocco maiuscole attivo» da mettere subito sotto un campo password (dentro lo stesso contenitore):
 * <input type="password" ... /><KernelCapsLockWarning />.
 * Compare appena si entra in un campo di quel contenitore (o si scrive) con il blocco maiuscole acceso, e sparisce
 * appena lo si spegne o si esce dal campo. Non serve collegarlo al campo: ascolta la tastiera da sé.
 */
function KernelCapsLockWarning() {
  const { t } = useLanguage();
  const ref = useRef(null);
  const [on, setOn] = useState(false);

  // Ultimo stato noto del blocco maiuscole: il browser lo dice solo dentro un evento di tastiera o di mouse,
  // quindi lo si ricorda da qualunque tasto o clic e lo si applica quando si entra nel campo (anche con un clic
  // o con Tab, prima ancora di scrivere).
  const lastCaps = useRef(false);

  useEffect(() => {
    const scoped = (target) => {
      const box = ref.current && ref.current.parentElement;
      return !!box && target instanceof Element && target.tagName === 'INPUT' && box.contains(target);
    };
    const update = (e) => {
      if (!e.getModifierState) return;
      const state = e.getModifierState('CapsLock');
      // Il tasto stesso: alla pressione alcuni sistemi riportano ancora lo stato di prima, quindi si
      // accende solo se già risulta acceso (Mac) e si lascia decidere al rilascio del tasto.
      if (e.type === 'keydown' && e.key === 'CapsLock') {
        if (state) {
          lastCaps.current = true;
          if (scoped(document.activeElement)) setOn(true);
        }
        return;
      }
      lastCaps.current = state;
      setOn(state && scoped(document.activeElement));
    };
    const onFocusIn = (e) => setOn(lastCaps.current && scoped(e.target));
    const onFocusOut = () => setOn(false);
    const events = ['keydown', 'keyup', 'mousedown', 'mouseup', 'click'];
    events.forEach((name) => document.addEventListener(name, update, true));
    document.addEventListener('focusin', onFocusIn, true);
    document.addEventListener('focusout', onFocusOut, true);
    return () => {
      events.forEach((name) => document.removeEventListener(name, update, true));
      document.removeEventListener('focusin', onFocusIn, true);
      document.removeEventListener('focusout', onFocusOut, true);
    };
  }, []);

  return (
    <div ref={ref} className="caps-lock-slot" role="status" aria-live="polite">
      {on && (
        <span className="caps-lock-warning">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 3 3 13h5v4h8v-4h5z" />
            <line x1="8" y1="21" x2="16" y2="21" />
          </svg>
          {t('capsLock.warning')}
        </span>
      )}
    </div>
  );
}

export default KernelCapsLockWarning;
